import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { first, map, Observable, Subscription } from 'rxjs';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';

import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { CreateUserDto, UserRole } from '../../interfaces/user/UserDto';
import { PDropdownOptions } from '../../interfaces';
import { UserHttpService } from '../../services';
import { TranslatePipe } from '@ngx-translate/core';
import { TranslationService } from '../../services/translation.service';

@Component({
  selector: 'add-user',
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgTemplateOutlet,
    NgClass,
    AsyncPipe,
    InputTextModule,
    SelectModule,
    ButtonModule,
    ToastModule,
    TranslatePipe
  ],
  providers: [MessageService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddUserComponent implements OnInit, OnDestroy {
  translateService = inject(TranslationService);
  @ViewChild('formError') formErrorTemplate!: TemplateRef<any>;
  createUserForm!: FormGroup;
  users: CreateUserDto[] = [];
  loadingData: boolean = true;

  subscription: Subscription[] = [];

  rolesOptions$: Observable<PDropdownOptions[]> = new Observable();

  public userService = inject(UserHttpService);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);

  constructor() { }
  langChangeSub!: Subscription;
  ngOnInit(): void {
    this.roleTranslation();

    this.langChangeSub = this.translateService.onLangChange.subscribe(() => {
      this.roleTranslation();
    });

    this.initForm();
  }

  ngOnDestroy(): void {
    this.subscription.forEach((sub) => sub.unsubscribe());
  }

  initForm(): void {
    this.createUserForm = this.fb.group({
      userName: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
        ]),
      ],
      email: [
        '',
        [
          Validators.compose([
            Validators.required,
            Validators.email,
            Validators.minLength(3),
            Validators.maxLength(320),
          ]),
        ],
      ],
      firstName: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
        ]),
      ],
      lastName: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
        ]),
      ],
      role: ['Administrator'],
      password: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
        ]),
      ],
    });
    this.loadingData = false;
  }

  createUser(): void {
    if (!this.createUserForm.valid) {
      return;
    }

    this.loadingData = true;
    const newUser = this.createUserForm.value;
    newUser.registerUrl = `https://${window.location.host}`;

    const createUserSubscription$ = this.userService
      .createUserV2(newUser)
      .subscribe(
        (response) => {
          //
          this.loadingData = false;
          this.createUserForm.reset();
          this.router.navigate(['/manage-users']);
        },
        (error) => {
          this.loadingData = false;
          console.error('Error creating user:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.getTranslation('TOAST.ERROR_TITLE'),
            detail: this.getBackendErrorMessage(error),
          });
        }
      );
    this.subscription.push(createUserSubscription$);

  }

  roleTranslation(): void {
    this.rolesOptions$ = this.userService.getUserRoles().pipe(
      first(),
      map((roles: UserRole[]) => {
        return roles.map((role) => ({
          key: role.name,
          value: role.name
        }));
      }),
      map((roleOptions) => {
        return roleOptions.map((option) => ({
          label: this.translateService.getTranslation(`roles.${option.key}`),
          value: option.value
        }));
      })
    )
  }

  get formError(): TemplateRef<any> {
    return this.formErrorTemplate;
  }

  private getBackendErrorMessage(error: any): string {
    const fallback = this.translateService.getTranslation('TOAST.SOMETHING_WENT_WRONG_TRY_AGAIN');
    const errorPayload = error?.error;

    if (typeof errorPayload === 'string' && errorPayload.trim()) {
      return errorPayload;
    }

    if (typeof errorPayload?.message === 'string' && errorPayload.message.trim()) {
      return errorPayload.message;
    }

    if (Array.isArray(errorPayload?.errors) && errorPayload.errors.length > 0) {
      const firstError = errorPayload.errors[0];
      if (typeof firstError === 'string' && firstError.trim()) {
        return firstError;
      }
    }

    if (errorPayload?.errors && typeof errorPayload.errors === 'object') {
      const firstKey = Object.keys(errorPayload.errors)[0];
      const firstValue = firstKey ? errorPayload.errors[firstKey] : null;
      if (Array.isArray(firstValue) && typeof firstValue[0] === 'string' && firstValue[0].trim()) {
        return firstValue[0];
      }
    }

    if (typeof error?.message === 'string' && error.message.trim()) {
      return error.message;
    }

    return fallback;
  }
}
